/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Chapter dropdown plus per-section question lists, built from host-supplied
 * `tocData` and `chapterMcqMapping`.
 *
 * @file QuestionPreview.jsx
 * @author Anuja Gaikwad
 * @since 3.0.0
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import QuestionPreviewChapterSelect from './QuestionPreviewChapterSelect';
import QuestionPreviewSection from './QuestionPreviewSection';
import { QuestionPreviewStylesRoot, classes } from './styles/QuestionPreviewStyles';
import {
  buildChapterOptions,
  buildChapterSections,
  getDefaultChapterId,
  getTocChapters
} from './questionPreviewUtils';
/**
 * Reusable question browser: a chapter dropdown above the selected chapter's sections, each
 * listing its questions. Data comes from the host in the same shape as the add-manually and
 * assisted scheduling flows (`tocData`, `chapterMcqMapping`), so no fetching happens here.
 *
 * Chapter selection works uncontrolled (defaults to the first chapter with questions) or
 * controlled through `selectedChapterId`. `onChapterChange` tells the host about user
 * changes, e.g. for analytics or lazy loading.
 *
 * UI only: the host wraps this in its own page chrome (e.g. DashboardCard with title,
 * back nav and AI disclaimer subtitle) and handles navigation.
 *
 * @param {Array} tocData book TOC tree; top-level `type: 'chapter'` nodes become dropdown options
 * @param {Array} chapterMcqMapping `[{ chapterId, questions: [{ questionId, pageId, questionStem, options }] }]`
 * @param {string} selectedChapterId optional controlled chapter id
 * @param {object} textInfo required localized copy from the host (see questionPreviewTextInfoShape)
 * @param {Function} onChapterChange called with the chapter id the user picked
 * @param {Function} onSectionExpand called with isExpanded when the user expands or collapses a section
 * @param {Function} onMCQExpand called with isExpanded when the user expands or collapses an MCQ card
 */
const QuestionPreview = ({
  className,
  tocData,
  chapterMcqMapping,
  selectedChapterId: selectedChapterIdProp,
  textInfo,
  onChapterChange,
  onSectionExpand,
  onMCQExpand
}) => {
  const chapterOptions = useMemo(
    () => buildChapterOptions(tocData, chapterMcqMapping),
    [tocData, chapterMcqMapping]
  );
  const [selectedChapterId, setSelectedChapterId] = useState(
    () => selectedChapterIdProp || getDefaultChapterId(chapterOptions)
  );

  // Controlled mode: follow the host's selection when it changes.
  useEffect(() => {
    if (selectedChapterIdProp) {
      setSelectedChapterId(selectedChapterIdProp);
    }
  }, [selectedChapterIdProp]);

  // TOC often arrives after first render (or changes book); re-pick a default when the selection is no longer valid.
  useEffect(() => {
    const isKnownChapter = chapterOptions.some((chapter) => chapter.id === selectedChapterId);

    if (!isKnownChapter) {
      setSelectedChapterId(getDefaultChapterId(chapterOptions));
    }
  }, [chapterOptions, selectedChapterId]);

  const sections = useMemo(() => {
    const chapterNode = getTocChapters(tocData).find((chapter) => chapter.id === selectedChapterId);

    return buildChapterSections(chapterNode, chapterMcqMapping);
  }, [tocData, chapterMcqMapping, selectedChapterId]);

  const handleChapterChange = (chapterId) => {
    setSelectedChapterId(chapterId);
    onChapterChange(chapterId);
  };

  return (
    <QuestionPreviewStylesRoot className={`${classes.main} ${className}`}>
      <QuestionPreviewChapterSelect
        chapters={chapterOptions}
        selectedChapterId={selectedChapterId}
        textInfo={textInfo}
        onChange={handleChapterChange}
      />

      <div className={classes.sectionsScroll}>
        {sections.length > 0 ? (
          // Keyed by chapter so section/card expand state resets when the chapter changes.
          <div key={selectedChapterId} className={classes.sections}>
            {sections.map((section, index) => (
              <QuestionPreviewSection
                key={section.id}
                section={section}
                textInfo={textInfo}
                defaultExpanded={
                  index === sections.findIndex((item) => item.questionCount > 0)
                }
                onSectionExpand={onSectionExpand}
                onMCQExpand={onMCQExpand}
              />
            ))}
          </div>
        ) : (
          <Typography component="p" className={classes.emptyMessage}>
            {textInfo.noQuestionsMessage}
          </Typography>
        )}
      </div>
    </QuestionPreviewStylesRoot>
  );
};

export const questionPreviewTextInfoShape = {
  chapterSelectLabel: PropTypes.string.isRequired,
  chapterSelectPlaceholder: PropTypes.string.isRequired,
  questionLabel: PropTypes.string.isRequired,
  questionsLabel: PropTypes.string.isRequired,
  correctAnswerLabel: PropTypes.string.isRequired,
  noQuestionsMessage: PropTypes.string.isRequired
};

QuestionPreview.propTypes = {
  className: PropTypes.string,
  tocData: PropTypes.arrayOf(PropTypes.object),
  chapterMcqMapping: PropTypes.arrayOf(PropTypes.shape({
    chapterId: PropTypes.string.isRequired,
    questions: PropTypes.array
  })),
  selectedChapterId: PropTypes.string,
  textInfo: PropTypes.shape(questionPreviewTextInfoShape).isRequired,
  onChapterChange: PropTypes.func,
  onSectionExpand: PropTypes.func,
  onMCQExpand: PropTypes.func
};

QuestionPreview.defaultProps = {
  className: '',
  tocData: [],
  chapterMcqMapping: [],
  selectedChapterId: '',
  onChapterChange: () => {},
  onSectionExpand: () => {},
  onMCQExpand: () => {}
};

export default QuestionPreview;
